/**
 * Backend de armazenamento persistente do store.
 *
 * Motivo: o estado completo ultrapassou a cota do localStorage (~5 MB). Mesmo
 * comprimido (ver persistCodec), conforme os dados crescem o localStorage pode
 * voltar a estourar. O IndexedDB tem cota de centenas de MB a GB.
 *
 * Desafio: o store inicializa SÍNCRONO no carregamento do módulo, e o IndexedDB
 * é assíncrono. Solução: `preloadStorage()` é chamado no boot (main.tsx) ANTES
 * de o store ser importado, carregando o blob para um cache síncrono em memória.
 * As leituras (`getRawStorage`) e escritas (`setRawStorage`) do store passam a
 * usar esse cache + IndexedDB, mantendo o boot síncrono.
 *
 * À PROVA DE FALHAS: se o IndexedDB estiver indisponível ou falhar, tudo cai
 * automaticamente no localStorage — o app se comporta exatamente como antes.
 * O localStorage também é mantido como espelho de melhor-esforço (pode estourar
 * a cota; nesse caso o erro é ignorado, pois o IndexedDB é a fonte durável).
 */

const DB_NAME = 'sistema-financeiro'
const DB_VERSION = 1
const STORE = 'kv'
const KEY = 'data'
/** Mesma chave usada historicamente no localStorage (compatibilidade/fallback). */
const LS_KEY = 'sistema-financeiro-data'
/** Acima disto não tenta o localStorage (estouraria a cota ~5 MB / 2 bytes por char). */
const LS_MAX_CHARS = 2_000_000

let cache: string | null = null
let cacheLoaded = false

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbGet(): Promise<string | null> {
  return openDB().then(
    (db) =>
      new Promise<string | null>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly')
        const r = tx.objectStore(STORE).get(KEY)
        r.onsuccess = () => {
          resolve((r.result as string | undefined) ?? null)
          db.close()
        }
        r.onerror = () => {
          reject(r.error)
          db.close()
        }
      })
  )
}

function idbSet(value: string): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite')
        tx.objectStore(STORE).put(value, KEY)
        tx.oncomplete = () => {
          resolve()
          db.close()
        }
        tx.onerror = () => {
          reject(tx.error)
          db.close()
        }
      })
  )
}

/**
 * Pré-carrega o blob persistido para o cache síncrono. DEVE ser chamado no boot,
 * ANTES de o store (e qualquer módulo que o importe) ser avaliado. Nunca rejeita.
 */
export async function preloadStorage(): Promise<void> {
  if (cacheLoaded) return
  try {
    if (typeof indexedDB !== 'undefined') {
      const fromIdb = await idbGet()
      if (fromIdb != null) {
        cache = fromIdb
        cacheLoaded = true
        return
      }
    }
  } catch {
    /* IndexedDB indisponível/erro → cai para o localStorage abaixo */
  }
  // Migração/fallback: primeira execução (IndexedDB vazio) ou IndexedDB indisponível.
  try {
    cache = localStorage.getItem(LS_KEY)
  } catch {
    cache = null
  }
  cacheLoaded = true
  // Semeia o IndexedDB com o que veio do localStorage, para as próximas sessões.
  if (cache != null) {
    try {
      void idbSet(cache)
    } catch {
      /* ignore */
    }
  }
}

/** Leitura SÍNCRONA do blob (cache pré-carregado; fallback localStorage). */
export function getRawStorage(): string | null {
  if (cache != null) return cache
  try {
    return localStorage.getItem(LS_KEY)
  } catch {
    return null
  }
}

/**
 * Grava o blob: cache (síncrono) + IndexedDB (durável, assíncrono) + localStorage
 * (espelho de melhor-esforço — pode estourar a cota e, nesse caso, é ignorado).
 */
export function setRawStorage(value: string): void {
  cache = value
  cacheLoaded = true
  try {
    void idbSet(value).catch((e) => {
      // Antes engolido: agora visível para diagnosticar perda de gravação local.
      console.error('[storage] gravação no IndexedDB falhou:', e)
    })
  } catch (e) {
    console.error('[storage] idbSet lançou exceção:', e)
  }
  // localStorage só como fallback para payloads PEQUENOS. Agora gravamos JSON puro
  // (sem compressão), então o store completo costuma passar de ~4 MB e estoura a
  // cota — nesse caso nem tenta (evita exceção e custo). O IndexedDB é o durável.
  if (value.length <= LS_MAX_CHARS) {
    try {
      localStorage.setItem(LS_KEY, value)
    } catch (e) {
      console.error('[storage] gravação no localStorage falhou (cota?):', e)
    }
  } else {
    try { localStorage.removeItem(LS_KEY) } catch { /* ignore */ }
  }
}

/**
 * Como {@link setRawStorage}, mas AGUARDA a gravação no IndexedDB concluir.
 * Usar antes de um `window.location.reload()` (ex.: hidratação) — senão o reload
 * pode ocorrer antes do commit no IndexedDB e a próxima sessão leria dado velho.
 */
export async function setRawStorageDurable(value: string): Promise<void> {
  cache = value
  cacheLoaded = true
  if (value.length <= LS_MAX_CHARS) {
    try {
      localStorage.setItem(LS_KEY, value)
    } catch {
      /* cota estourada — IndexedDB abaixo é a fonte durável */
    }
  } else {
    try { localStorage.removeItem(LS_KEY) } catch { /* ignore */ }
  }
  try {
    await idbSet(value)
  } catch {
    /* ignore — ao menos o localStorage (se coube) e o cache têm o valor */
  }
}
